// MastiBazaar — async data layer (backed by FastAPI + MongoDB).
// Same exported shape as the old localStorage version so consumers (Home,
// AdminPanel, CheckoutDialog) only swap sync→async.
import {
  apiCreateOrder,
  apiCreateProduct,
  apiDeleteProduct,
  apiGetSiteLive,
  apiListOrders,
  apiListProducts,
  apiSetSiteLive,
  apiUpdateStock,
  apiUploadIfDataUrl,
} from "@/lib/api";

const MIGRATION_KEY = "mb_migrated_v1";
const LEGACY_PRODUCTS_KEY = "mb_products_v1";
const LEGACY_ORDERS_KEY = "mb_orders_v1";
const LEGACY_REVIEWS_KEY = "mb_reviews_v1";

const safeReadArray = (key) => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const parseSizes = (raw) => {
  if (Array.isArray(raw)) {
    return raw.map((s) => String(s).trim()).filter(Boolean);
  }
  if (typeof raw === "string") {
    return raw.split(",").map((s) => s.trim()).filter(Boolean);
  }
  return [];
};

const parseColors = (raw) => {
  if (Array.isArray(raw)) {
    return raw.map((s) => String(s).trim()).filter(Boolean).slice(0, 6);
  }
  if (typeof raw === "string") {
    return raw.split(",").map((s) => s.trim()).filter(Boolean).slice(0, 6);
  }
  return [];
};

const parseStockMap = (raw, sizes) => {
  if (!raw) return {};
  if (typeof raw === "object" && !Array.isArray(raw)) {
    const out = {};
    for (const [k, v] of Object.entries(raw)) {
      const n = Number(v);
      if (!Number.isNaN(n)) out[k] = n;
    }
    return out;
  }
  if (typeof raw === "string") {
    // "S:10,M:5"
    const out = {};
    raw.split(",").forEach((pair) => {
      const [k, v] = pair.split(":").map((s) => s.trim());
      if (k && v && !Number.isNaN(Number(v))) out[k] = Number(v);
    });
    return out;
  }
  return {};
};

// PRODUCTS
// Public storefront uses `getProducts()` → only ACTIVE products.
// Admin pulls everything via `getAllProducts()`.
export const getProducts = async () => apiListProducts({ includeInactive: false });
export const getAllProducts = async () =>
  apiListProducts({ includeInactive: true });

export const addProduct = async ({
  title,
  price,
  originalPrice,
  category,
  description,
  image,
  sizes,
  colors,
  stock_by_size,
  images,
  color_names,
}) => {
  const sizesArr = parseSizes(sizes);
  const colorsArr = parseColors(colors);
  // Upload main image if dataURL
  const finalImage = await apiUploadIfDataUrl(image);
  // Upload each color variant if dataURL
  const finalColors = [];
  for (const c of colorsArr) {
    finalColors.push(await apiUploadIfDataUrl(c));
  }
  // NEW additive: upload each extra gallery image (skip empties / data-urls handled).
  const galleryArr = Array.isArray(images)
    ? images.map((s) => String(s).trim()).filter(Boolean)
    : [];
  const finalImages = [];
  for (const g of galleryArr) {
    finalImages.push(await apiUploadIfDataUrl(g));
  }
  // NEW additive: text-only color names (trimmed, deduped, max 12).
  const colorNamesArr = Array.isArray(color_names)
    ? Array.from(
        new Set(
          color_names
            .map((s) => String(s).trim())
            .filter(Boolean),
        ),
      ).slice(0, 12)
    : [];
  return apiCreateProduct({
    title: String(title || "").trim(),
    price: Number(price) || 0,
    original_price: Number(originalPrice) > 0 ? Number(originalPrice) : null,
    category: String(category || "General").trim(),
    description: String(description || "").trim(),
    image: finalImage,
    sizes: sizesArr,
    colors: finalColors,
    images: finalImages,
    color_names: colorNamesArr,
    stock_by_size: parseStockMap(stock_by_size, sizesArr),
  });
};

export const deleteProduct = async (id) => apiDeleteProduct(id);

export const updateStock = async (id, stockBySize) =>
  apiUpdateStock(id, stockBySize);

// ORDERS
export const getOrders = async () => apiListOrders();

export const addOrder = async ({
  product,
  items,
  name,
  phone,
  email,
  altPhone,
  pincode,
  state,
  city,
  house,
  colony,
  address,
}) => {
  const normalisedItems =
    Array.isArray(items) && items.length > 0
      ? items.map((i) => ({
          productId: i.id,
          title: i.title,
          image: i.image,
          price: Number(i.price) || 0,
          qty: Number(i.qty) || 1,
          size: i.size || "Free Size",
          color: i.color || null,
          colorLabel: i.colorLabel || null,
        }))
      : product
        ? [
            {
              productId: product.id,
              title: product.title,
              image: product.image,
              price: Number(product.price) || 0,
              qty: 1,
              size: product.selectedSize || "Free Size",
              color: product.selectedColor || null,
              colorLabel: product.selectedColorLabel || null,
            },
          ]
        : [];

  return apiCreateOrder({
    items: normalisedItems,
    name: String(name || "").trim(),
    phone: String(phone || "").trim(),
    email: String(email || "").trim(),
    altPhone: String(altPhone || "").trim(),
    pincode: String(pincode || "").trim(),
    state: String(state || "").trim(),
    city: String(city || "").trim(),
    house: String(house || "").trim(),
    colony: String(colony || "").trim(),
    address: String(address || "").trim(),
    payment: "COD",
    status: "Placed",
  });
};

// MAINTENANCE / LAUNCH MODE
export const isSiteLive = async () => apiGetSiteLive();

export const setSiteLive = async (live) => {
  await apiSetSiteLive(live);
  try {
    window.dispatchEvent(new Event("mb-maintenance-change"));
  } catch {
    /* noop */
  }
};

// MIGRATION — reads legacy localStorage and posts to /api/migrate.
// Idempotent: sets mb_migrated_v1=1 after the first successful run.
export const hasMigrated = () => {
  try {
    return localStorage.getItem(MIGRATION_KEY) === "1";
  } catch {
    return true;
  }
};

export const markMigrated = () => {
  try {
    localStorage.setItem(MIGRATION_KEY, "1");
  } catch {
    /* noop */
  }
};

export const readLegacySnapshot = () => ({
  products: safeReadArray(LEGACY_PRODUCTS_KEY),
  orders: safeReadArray(LEGACY_ORDERS_KEY),
  reviews: safeReadArray(LEGACY_REVIEWS_KEY),
});
